import * as THREE from 'three'
import {
  defaultAnchorSpec,
  mat4,
  PoseSmoother,
  solveGlasses,
  type AnchorSpec,
  type CameraModel,
  type FaceFrame,
  type GlassesSolveResult,
  type GlassesSolverOptions,
  type POSE_SMOOTHER_PRESETS,
  type PoseSmootherConfig,
  type Vec3,
} from '@ar-suite/core'
import { applyPose, ARScene, applyMaterialOverrides, disposeObject, FaceOccluder } from '@ar-suite/render-web'
import { createLensMaterial, NODE } from './procedural'

export interface GlassesModuleOptions {
  spec?: AnchorSpec
  smoothing?: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS
  solver?: Partial<GlassesSolverOptions>
}

const ORIGIN = new THREE.Vector3()
const UP = new THREE.Vector3(0, 1, 0)

interface TempleBinding {
  node: THREE.Object3D
  axis: 0 | 1 | 2
  sign: 1 | -1
  /** Node-local length along the axis at scale 1. */
  localLength: number
  side: 'left' | 'right'
  procedural: boolean
}

/**
 * Glasses try-on module: solver (core) → smoother → three.js adapter (pose, temples, lenses, occluder spec).
 * One instance per rendered pair. Feed `update()` every tracked frame.
 */
export class GlassesModule {
  readonly root = new THREE.Group()
  readonly modelHolder = new THREE.Group()
  readonly smoother: PoseSmoother
  private _spec: AnchorSpec
  private solverOptions: Partial<GlassesSolverOptions>
  private model: THREE.Object3D | null = null
  private temples: TempleBinding[] = []
  private result: GlassesSolveResult | null = null
  private earWorld = new THREE.Vector3()
  private hingeWorld = new THREE.Vector3()
  private anchorMat = mat4.create()
  private occluder: FaceOccluder | null = null
  private lensMaterials: THREE.MeshPhysicalMaterial[] = []
  private lookAtMat = new THREE.Matrix4()

  constructor(readonly scene: ARScene, options: GlassesModuleOptions = {}) {
    this._spec = options.spec ?? defaultAnchorSpec('glasses')
    this.smoother = new PoseSmoother(options.smoothing ?? 'balanced')
    this.solverOptions = options.solver ?? {}
    this.root.name = 'glasses-anchor'
    this.modelHolder.name = 'glasses-model'
    this.root.add(this.modelHolder)
    this.root.visible = false
    scene.content.add(this.root)
  }

  get spec(): AnchorSpec {
    return this._spec
  }
  get lastResult(): GlassesSolveResult | null {
    return this.result
  }
  get hasModel(): boolean {
    return this.model !== null
  }
  get visible(): boolean {
    return this.root.visible
  }
  set visible(v: boolean) {
    this.root.visible = v
  }

  /** Attach the face occluder whose spec this module controls (shared with other face modules). */
  attachOccluder(occluder: FaceOccluder): void {
    this.occluder = occluder
    occluder.setSpec(this._spec.occluder)
  }

  setSpec(spec: AnchorSpec): void {
    this._spec = spec
    this.occluder?.setSpec(spec.occluder)
    this.applyLensSpec()
    this.rebindTemples()
  }

  /** Mutate the current spec (fit sliders) without replacing the object. */
  patchSpec(patch: Partial<AnchorSpec>): void {
    Object.assign(this._spec, patch)
    this.setSpec(this._spec)
  }

  setSmoothing(cfg: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS): void {
    this.smoother.configure(cfg)
  }

  /** Replace the product model (procedural group or GLB root; must be authored in cm at this point). */
  setModel(model: THREE.Object3D | null): void {
    if (this.model) {
      this.modelHolder.remove(this.model)
      disposeObject(this.model)
    }
    this.model = model
    this.temples = []
    this.lensMaterials = []
    if (model) {
      this.modelHolder.add(model)
      this.rebindTemples()
      this.applyLensSpec()
    }
  }

  applyVariantMaterials(overrides: Record<string, { color?: string; metalness?: number; roughness?: number; opacity?: number }>): void {
    if (this.model) applyMaterialOverrides(this.model, overrides)
  }

  /**
   * Solve + smooth + apply for one frame.
   * @param tMs frame timestamp (ms)
   */
  update(frame: FaceFrame, cam: CameraModel, tMs: number): GlassesSolveResult {
    this.result = solveGlasses(frame, cam, this._spec, this.solverOptions, this.result ?? undefined)
    const smoothed = this.smoother.update(this.result.pose, tMs)
    applyPose(this.root, smoothed)
    this.root.visible = true
    if (this._spec.glasses?.autoFitTemples !== false && this.temples.length) this.fitTemples(smoothed.position, smoothed.rotation)
    return this.result
  }

  /** Called when tracking is lost: hide and reset the filters so re-acquire doesn't swoop in. */
  onTrackingLost(): void {
    this.root.visible = false
    this.smoother.reset()
  }

  /** Bones/nodes to align: procedural temples (unit-length arms) or GLB temple nodes from the spec. */
  private rebindTemples(): void {
    this.temples = []
    if (!this.model) return
    const names = this._spec.glasses?.templeNodes ?? [NODE.templeL, NODE.templeR]
    const [leftName, rightName] = names
    for (const [name, side] of [[leftName, 'left'], [rightName, 'right']] as const) {
      const node = this.model.getObjectByName(name)
      if (!node) continue
      if (node.userData.unitLengthArm) {
        this.temples.push({ node, axis: 2, sign: -1, localLength: 1, side, procedural: true })
        continue
      }
      // GLB temple: measure its dominant axis in node-local space
      const box = new THREE.Box3()
      const saved = node.scale.clone()
      node.scale.set(1, 1, 1)
      node.updateMatrixWorld(true)
      const inv = new THREE.Matrix4().copy(node.matrixWorld).invert()
      node.traverse((o) => {
        const m = o as THREE.Mesh
        if (!m.isMesh || !m.geometry) return
        m.geometry.computeBoundingBox()
        const bb = m.geometry.boundingBox!.clone().applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld))
        box.union(bb)
      })
      node.scale.copy(saved)
      if (box.isEmpty()) continue
      const size = box.getSize(new THREE.Vector3())
      const axis = (size.x >= size.y && size.x >= size.z ? 0 : size.y >= size.z ? 1 : 2) as 0 | 1 | 2
      const min = box.min.getComponent(axis), max = box.max.getComponent(axis)
      // temple extends from the hinge (node origin) toward whichever side is farther
      const sign = Math.abs(max) >= Math.abs(min) ? 1 : -1
      this.temples.push({ node, axis, sign, localLength: Math.max(Math.abs(max), Math.abs(min)), side, procedural: false })
    }
  }

  private fitTemples(anchorPos: Vec3, anchorRot: [number, number, number, number]): void {
    if (!this.result) return
    mat4.compose(this.anchorMat, anchorPos, anchorRot, 1)
    this.root.updateMatrixWorld(true)
    const tmp: Vec3 = [0, 0, 0]
    for (const t of this.temples) {
      const ear = t.side === 'left' ? this.result.fit.earLeft : this.result.fit.earRight
      mat4.transformPoint(tmp, this.anchorMat, ear)
      this.earWorld.set(tmp[0], tmp[1], tmp[2])
      t.node.getWorldPosition(this.hingeWorld)
      const targetLen = this.hingeWorld.distanceTo(this.earWorld)
      if (t.procedural) {
        // aim the temple group at the ear, arm scaled to length
        const parent = t.node.parent!
        const earLocal = parent.worldToLocal(this.earWorld.clone())
        const dir = earLocal.clone().sub(t.node.position)
        const len = dir.length()
        if (len < 1e-3) continue
        // Matrix4.lookAt(eye, target, up) makes +Z point from target to eye, i.e. −Z (the arm) points at the target.
        // Up hint keeps the temple upright (no roll around its own axis).
        this.lookAtMat.lookAt(ORIGIN, dir, UP)
        t.node.quaternion.setFromRotationMatrix(this.lookAtMat)
        const arm = t.node.getObjectByName(t.node.userData.unitLengthArm as string)
        if (arm) arm.scale.z = len
        const tip = t.node.getObjectByName(t.node.userData.tip as string)
        if (tip) tip.position.z = -len - 0.2
      } else {
        // GLB: scale along the dominant local axis so the tip reaches the ear
        const parentScale = t.node.parent ? t.node.parent.getWorldScale(new THREE.Vector3()) : new THREE.Vector3(1, 1, 1)
        const worldLen1 = t.localLength * parentScale.getComponent(t.axis)
        if (worldLen1 <= 1e-6) continue
        const s = THREE.MathUtils.clamp(targetLen / worldLen1, 0.6, 1.6)
        t.node.scale.setComponent(t.axis, s)
      }
    }
  }

  private applyLensSpec(): void {
    if (!this.model) return
    const g = this._spec.glasses
    const names = g?.lensNodes ?? [NODE.lensL, NODE.lensR]
    const opacity = g?.lensOpacity ?? 0.18
    const tint = g?.lensTint
    this.lensMaterials = []
    for (const name of names) {
      const node = this.model.getObjectByName(name) as THREE.Mesh | undefined
      if (!node?.isMesh) continue
      let mat = node.material as THREE.MeshPhysicalMaterial
      if (!mat.isMeshPhysicalMaterial || !mat.userData.arLens) {
        mat = createLensMaterial(tint ?? 0x88aacc, opacity, this.scene.environmentTexture)
        node.material = mat
        node.renderOrder = 10
      }
      if (!mat.envMap) mat.envMap = this.scene.environmentTexture
      mat.opacity = opacity
      mat.transparent = true
      if (tint) mat.color.set(tint)
      this.lensMaterials.push(mat)
    }
  }

  dispose(): void {
    this.setModel(null)
    this.scene.content.remove(this.root)
  }
}
